import { useState } from 'react';

import { AppModal } from '../AppModal';

// Document-agnostic approve / return / reject decision. Generalized from the #4
// JV `JvDecision` — both JV and payreq drive their decide flow through it.
//
// M7-P3 (SoD-by-actorId): `sodOverrideReason` is an OPTIONAL, additive field set
// only after the engine returns a separation-of-duties 403 and an authorized user
// supplies a reason on retry. Callers that thread it into their mutation body get
// the audited override; callers that ignore it are unchanged.
export type Decision =
  | { action: 'approve'; step: number; comment: string; sodOverrideReason?: string }
  | { action: 'return'; step: number; reason: string; sodOverrideReason?: string }
  | { action: 'reject'; step: number; reason: string; sodOverrideReason?: string };

// The engine's SoD-block messages all carry this Latin marker (the Thai copy
// varies per rule but every SoD throw appends "(separation of duties)").
function isSodError(error: string | null): boolean {
  return !!error && error.toLowerCase().includes('separation of duties');
}

interface Props {
  voucherNo: string;
  step: number;
  busy: boolean;
  error: string | null;
  onConfirm: (d: Decision) => void;
  onClose: () => void;
  /** Title prefix before " — {voucherNo}". Default = JV copy. */
  titlePrefix?: string;
  /** Renders the subtitle for the current step. Default = `ขั้นที่ {step}`. */
  stepLabel?: (step: number) => string;
  /** Hide the "ตีกลับแก้ไข" (return) option for docs with no resubmit lane. */
  hideReturn?: boolean;
}

const OPTIONS: { key: 'approve' | 'return' | 'reject'; label: string; color: string }[] = [
  { key: 'approve', label: 'อนุมัติ', color: 'var(--positive)' },
  { key: 'return', label: 'ตีกลับแก้ไข', color: 'var(--warning)' },
  { key: 'reject', label: 'ไม่อนุมัติ', color: 'var(--negative)' },
];

export function DecisionModal({
  voucherNo,
  step,
  busy,
  error,
  onConfirm,
  onClose,
  titlePrefix = 'พิจารณาใบสำคัญ',
  stepLabel = (s) => `ขั้นที่ ${s}`,
  hideReturn = false,
}: Props) {
  const [action, setAction] = useState<'approve' | 'return' | 'reject'>('approve');
  const [text, setText] = useState('');
  // M7-P3: only revealed after a SoD 403; carries the audited override reason.
  const [sodReason, setSodReason] = useState('');
  const options = hideReturn ? OPTIONS.filter((o) => o.key !== 'return') : OPTIONS;
  const reasonRequired = action !== 'approve';
  const confirmDisabled = busy || (reasonRequired && !text.trim());
  const showOverride = isSodError(error);

  function confirm() {
    const override =
      showOverride && sodReason.trim() ? { sodOverrideReason: sodReason.trim() } : {};
    if (action === 'approve') onConfirm({ action, step, comment: text, ...override });
    else if (action === 'return') onConfirm({ action, step, reason: text, ...override });
    else onConfirm({ action, step, reason: text, ...override });
  }

  return (
    <AppModal
      title={`${titlePrefix} — ${voucherNo}`}
      subtitle={stepLabel(step)}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            type="button"
            className="btn primary"
            disabled={confirmDisabled}
            style={action === 'reject' ? { background: 'var(--negative)' } : undefined}
            onClick={confirm}
          >
            {busy ? 'กำลังบันทึก…' : 'ยืนยัน'}
          </button>
        </>
      }
    >
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {options.map((o) => (
          <button
            key={o.key}
            type="button"
            onClick={() => setAction(o.key)}
            style={{
              flex: 1,
              padding: '10px 12px',
              borderRadius: 8,
              cursor: 'pointer',
              border: action === o.key ? `2px solid ${o.color}` : '1px solid var(--border)',
              background: action === o.key ? `${o.color}15` : 'white',
              fontWeight: 600,
            }}
          >
            {action === o.key ? '● ' : ''}
            {o.label}
          </button>
        ))}
      </div>
      <label className="meta" style={{ display: 'block', marginBottom: 6 }}>
        {reasonRequired ? 'เหตุผล *' : 'หมายเหตุ (ถ้ามี)'}
      </label>
      <textarea
        className="input"
        rows={3}
        value={text}
        placeholder={reasonRequired ? 'เหตุผลที่ตีกลับ/ไม่อนุมัติ' : 'หมายเหตุ'}
        onChange={(e) => setText(e.target.value)}
        style={{ width: '100%', resize: 'vertical', padding: 8 }}
      />
      {error && <div style={{ color: 'var(--negative)', fontSize: 14, marginTop: 8 }}>{error}</div>}
      {showOverride && (
        <div style={{ marginTop: 12 }}>
          <label
            htmlFor="sod-override-reason"
            className="meta"
            style={{ display: 'block', marginBottom: 6, color: 'var(--warning)' }}
          >
            เหตุผล override (SoD)
          </label>
          <textarea
            id="sod-override-reason"
            className="input"
            rows={2}
            value={sodReason}
            placeholder="ระบุเหตุผลที่ต้องอนุมัติแทน (เช่น ผู้อนุมัติเพียงคนเดียว)"
            onChange={(e) => setSodReason(e.target.value)}
            style={{ width: '100%', resize: 'vertical', padding: 8 }}
          />
        </div>
      )}
    </AppModal>
  );
}
